import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useSubscription } from '../hooks/useSubscription';

const BANNER_HEIGHT = 36;

const linkButtonStyle = {
  background: 'none',
  border: 'none',
  color: '#29458d',
  fontWeight: 700,
  textDecoration: 'underline',
  cursor: 'pointer',
  padding: 0,
  font: 'inherit',
};

function getBannerContent({ status, isEarly, trialDaysRemaining, onStartTrial }) {
  if (isEarly && (status === 'active' || status === 'trialing')) {
    const heart = (
      <svg
        aria-hidden="true"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="#29458d"
        style={{ verticalAlign: 'middle' }}
      >
        <path d="M12 21s-7-4.35-9.5-9.5C.83 8.14 2.74 4.5 6.5 4.5c2 0 3.5 1 5.5 3 2-2 3.5-3 5.5-3 3.76 0 5.67 3.64 4 7C19 16.65 12 21 12 21z" />
      </svg>
    );
    return {
      message: (
        <>
          {heart}
          <span>Thanks for being an Early Supporter — your rate stays $4.99/month</span>
          {heart}
        </>
      ),
      background: 'linear-gradient(90deg, #c8902e 0%, #e0a83a 50%, #c8902e 100%)',
      color: '#fff',
      textShadow: '0 1px 1px rgba(0,0,0,0.1)',
    };
  }

  if (status === 'trialing') {
    const daysText =
      trialDaysRemaining === 0
        ? 'Your free trial ends today.'
        : trialDaysRemaining === 1
        ? '1 day left in your free trial.'
        : `${trialDaysRemaining} days left in your free trial.`;
    return {
      message: <span>{daysText}</span>,
      background: '#dbc078',
      color: '#29458d',
    };
  }

  if (status === 'active') {
    return {
      message: <span>You&apos;re subscribed to Pre Go Prep Premium — thanks for supporting us!</span>,
      background: '#dbc078',
      color: '#29458d',
    };
  }

  if (status === 'past_due') {
    return {
      message: <span>Payment failed — please update your payment method to keep SMS reminders active.</span>,
      background: '#fff4e5',
      color: '#92400e',
    };
  }

  if (status === 'incomplete') {
    return {
      message: (
        <span>Your subscription couldn&apos;t be activated — please update your payment method to continue.</span>
      ),
      background: '#fff4e5',
      color: '#92400e',
    };
  }

  if (status === 'canceled' || status === 'incomplete_expired') {
    const cancelMessage =
      status === 'incomplete_expired'
        ? 'Your previous subscription attempt didn’t go through.'
        : 'Your subscription is canceled. The calculator and downloads remain free.';
    return {
      message: (
        <span>
          {cancelMessage}
          {status === 'incomplete_expired' && (
            <>
              {' '}
              <button type="button" onClick={onStartTrial} style={linkButtonStyle}>
                Try again
              </button>{' '}
              to start your free trial.
            </>
          )}
        </span>
      ),
      background: '#f1f3f5',
      color: '#495057',
    };
  }

  if (status === 'none') {
    return {
      message: (
        <span>
          You&apos;re on the Core plan.{' '}
          <button type="button" onClick={onStartTrial} style={linkButtonStyle}>
            Start your free trial
          </button>{' '}
          to unlock SMS race reminders.
        </span>
      ),
      background: '#dbc078',
      color: '#29458d',
    };
  }

  return null;
}

export default function EarlySupporterBanner() {
  const { user } = useAuth();
  const { subscription, loading, trialDaysRemaining } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const status = subscription?.subscription_status || 'none';
  const isEarly = subscription?.price_tier === 'early_supporter';

  const handleStartTrial = () => {
    const scrollToSection = () => {
      const el = document.getElementById('subscription-billing');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (location.pathname === '/profile') {
      scrollToSection();
    } else {
      navigate('/profile');
      // Wait for Profile to mount before scrolling.
      setTimeout(scrollToSection, 200);
    }
  };

  const content = !user || loading
    ? null
    : getBannerContent({ status, isEarly, trialDaysRemaining, onStartTrial: handleStartTrial });
  const shouldShow = !!content;

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--early-banner-height',
      shouldShow ? `${BANNER_HEIGHT}px` : '0px'
    );
    return () => {
      document.documentElement.style.setProperty('--early-banner-height', '0px');
    };
  }, [shouldShow]);

  if (!shouldShow) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: `${BANNER_HEIGHT}px`,
        background: content.background,
        color: content.color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '10px',
        fontSize: '0.85rem',
        fontWeight: 500,
        letterSpacing: '0.2px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        zIndex: 1300,
        textShadow: content.textShadow,
        padding: '0 16px',
        textAlign: 'center',
      }}
    >
      {content.message}
    </div>
  );
}
